'use client';

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { PageHeader } from '@/components/ui/page-header';
import { toast } from '@/components/ui/use-toast';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInvoices } from '@crm-src/hooks/useInvoices';
import { GenerateInvoiceForm } from '@crm-src/components/generate-invoice-form';
import {
  validateOttoInvoiceDraft,
  type OttoInvoiceDraft,
} from '@crm-src/components/generate-invoice-form/data-loaders';
import { consumeOttoDraft } from '@crm-src/lib/otto-draft-handoff';
import api from '@crm-src/lib/api';
import { invoiceSchedulesApi } from '@crm-src/lib/invoice-schedules-api';
import { BackButton } from "@/components/ui/back-button";

const GenerateInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { createNewInvoice, getInvoiceStats } = useInvoices();
  const [isCreating, setIsCreating] = useState(false);
  const [preSelectedCustomerId, setPreSelectedCustomerId] = useState<string | null>(null);
  // `?resellerId=` — raise the document against a RESELLER itself rather than a
  // customer (2026-09-13). Read straight off the params: unlike the ids above
  // it selects a MODE, and nothing later in the page can change it.
  const billToResellerId = searchParams.get('resellerId');
  const [preSelectedDocumentType, setPreSelectedDocumentType] = useState<'invoice' | 'quote'>('invoice');
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [isConvertingFromQuote, setIsConvertingFromQuote] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState<string | null>(null);
  const [duplicateFrom, setDuplicateFrom] = useState<string | null>(null);
  const [ottoDraft, setOttoDraft] = useState<OttoInvoiceDraft | null>(null);

  // Otto Agent handoff: a fully-formed draft stashed under `?draft=<nonce>`. Read
  // + consume it once (single-use) so the human lands on a pre-filled, ready-to-
  // save invoice. See docs/otto-agent.md.
  useEffect(() => {
    const nonce = searchParams.get('draft');
    if (!nonce) return;
    try {
      const draft = validateOttoInvoiceDraft(consumeOttoDraft(nonce));
      setOttoDraft({ ...draft, document_type: 'invoice' });
    } catch (error) {
      console.error('Invalid Otto invoice draft:', error);
      toast({ title: 'Could not open the draft', description: 'Otto’s draft was incomplete.', variant: 'destructive' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Check for customerId, documentType, invoiceId, quoteId, and duplicateFrom in URL parameters
  useEffect(() => {
    const customerId = searchParams.get('customerId');
    const documentType = searchParams.get('documentType') as 'invoice' | 'quote';
    const editInvoiceId = searchParams.get('invoiceId');
    const quoteId = searchParams.get('quoteId'); // For converting quote to invoice
    const duplicateFromId = searchParams.get('duplicateFrom'); // For duplicating invoice


    if (customerId) {
      setPreSelectedCustomerId(customerId);
    }
    
    // If duplicating from invoice, set it as duplicateFrom (NOT invoiceId)
    if (duplicateFromId) {
      setDuplicateFrom(duplicateFromId); // Load invoice data for duplication
      setInvoiceId(null); // Keep in CREATE mode, not edit mode
      setQuoteToConvert(null);
      setIsConvertingFromQuote(false);
    }
    // If converting from quote, set it as quoteToConvert (NOT invoiceId)
    else if (quoteId) {
      setQuoteToConvert(quoteId); // Load quote data for duplication
      setInvoiceId(null); // Keep in CREATE mode, not edit mode
      setPreSelectedDocumentType('invoice'); // Force document type to invoice
      setIsConvertingFromQuote(true); // Lock document type selector
      setDuplicateFrom(null);
    } else if (documentType === 'quote' || documentType === 'invoice') {
      setPreSelectedDocumentType(documentType);
      setIsConvertingFromQuote(false);
      setQuoteToConvert(null);
      setDuplicateFrom(null);
    }
    
    if (editInvoiceId && !quoteId && !duplicateFromId) {
      setInvoiceId(editInvoiceId);
      setQuoteToConvert(null);
      setDuplicateFrom(null);
    }
  }, [searchParams]);

  // Handle form submission - this is the page-specific logic
  const handleSubmit = async (invoiceData: any) => {
    try {
      setIsCreating(true);

      // Extract scheduling data before creating invoice
      const schedulingData = invoiceData.scheduling;
      delete invoiceData.scheduling;

      const createdInvoice = await createNewInvoice(invoiceData);

      // If converting from quote, mark the original quote as "converted"
      if (quoteToConvert) {
        try {
          await api.patch(`/invoices/${quoteToConvert}`, {
            invoice: { status: 'converted' }
          });
          // Refresh quote stats too
          getInvoiceStats({ document_type: 'quote' });
        } catch (error) {
          console.error('Failed to mark quote as converted:', error);
          // Don't fail the whole operation if this fails
        }
      }

      // Handle scheduling if enabled. Recurring schedules CREATE invoices on a
      // cadence — they never send to the customer (each generated invoice is
      // emailed to finance for review), so there is no one-off send / auto-send
      // / recipient config here anymore.
      let schedulingMessage = '';
      if (schedulingData && createdInvoice?.id) {
        // Handle recurring schedule
        if (schedulingData.recurring) {
          try {
            const recurringResponse = await invoiceSchedulesApi.create({
              customer_id: invoiceData.customer_id,
              source_invoice_id: createdInvoice.id,
              name: `Recurring - ${invoiceData.invoice_number}`,
              frequency: schedulingData.recurring.frequency,
              interval: schedulingData.recurring.interval,
              day_of_week: schedulingData.recurring.day_of_week,
              day_of_month: schedulingData.recurring.day_of_month,
              start_date: schedulingData.recurring.start_date,
              payment_terms_days: 30
            });
            if (recurringResponse.success) {
              schedulingMessage = ' with recurring billing set up';
            }
          } catch (error) {
            console.error('Failed to create recurring schedule:', error);
            toast({
              title: 'Warning',
              description: 'Invoice created but recurring setup failed. You can set it up later.',
              variant: 'default'
            });
          }
        }
      }

      toast({
        title: 'Success',
        description: duplicateFrom
          ? 'Invoice duplicated successfully' + schedulingMessage
          : quoteToConvert
            ? 'Invoice created from quote successfully' + schedulingMessage
            : 'Invoice generated successfully' + schedulingMessage
      });

      // Refresh stats and redirect back appropriately
      getInvoiceStats({ document_type: 'invoice' });
      if (preSelectedCustomerId) {
        navigate(`/crm/customers/${preSelectedCustomerId}`);
      } else {
        navigate('/crm/invoices');
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error creating invoice:', error);

      // Report the server's validation messages back to the form so it
      // can show them in its persistent banner + on the due-date field.
      // (Previously this toasted them and the toast auto-dismissed, so
      // the errors "never appeared on screen".) `createInvoice`'s thunk
      // rejects with `{ error, errors:[...] }`; axios failures expose
      // `response.data.errors`.
      const data = error?.response?.data;
      let errors: string[];
      if (Array.isArray(error?.errors) && error.errors.length > 0) {
        errors = error.errors;
      } else if (Array.isArray(data?.errors) && data.errors.length > 0) {
        errors = data.errors;
      } else {
        errors = [
          error?.error || data?.error || error?.message || 'Failed to generate invoice',
        ];
      }
      return { success: false, errors };
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    // If we came from a customer page, go back to that customer
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/crm/invoices');
    }
  };

  const handleBack = () => {
    // If we came from a customer page, go back to that customer
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/invoices');
    }
  };

  const handleInvoiceUpdated = () => {
    toast({
      title: 'Success',
      description: 'Invoice updated successfully'
    });

    // Refresh stats and redirect back appropriately
    getInvoiceStats();
    if (preSelectedCustomerId) {
      navigate(`/crm/customers/${preSelectedCustomerId}`);
    } else {
      navigate('/crm/invoices');
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={invoiceId ? 'Edit Document' : 'Generate Document'}
        description={invoiceId ? 'Update invoice or quote details' : 'Create a professional invoice or quote with all details'}
        actions={
          <BackButton onClick={handleBack} />
        }
      />

      {/* Form Content - Use reusable component but wrap it in page layout */}
      <Box sx={{ bgcolor: "background.paper", p: 4, borderRadius: 1, boxShadow: 1, border: 1, borderColor: "divider" }}>
        <GenerateInvoiceForm
          onBack={handleBack}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          isCreating={isCreating}
          preSelectedCustomerId={preSelectedCustomerId}
          preSelectedDocumentType={preSelectedDocumentType}
          invoiceId={invoiceId}
          onInvoiceUpdated={handleInvoiceUpdated}
          isConvertingFromQuote={isConvertingFromQuote}
          quoteToConvert={quoteToConvert}
          duplicateFrom={duplicateFrom}
          ottoDraft={ottoDraft}
          billToResellerId={billToResellerId}
        />
      </Box>
    </Box>
  );
};

export default GenerateInvoicePage;
